import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useFlag } from '@innovamat/flags';
import type { Organization } from '@innovamat/glow-api-client';
import { useSitemapTreeQuery } from '@innovamat/glow-api-client';
import { stages } from '@innovamat/radiance-utils';

import {
  useClassroom,
  useClassroomCapabilities,
  useOrganization,
} from '../../classrooms-management';
import { usePermissionCourses } from '../../classrooms-management/hooks/use-permission-courses';
import {
  isJourneyClassroom,
  type Classroom,
} from '../../classrooms-management/modules/classroom';
import { isJourneysEnabled } from '../../classrooms-management/modules/permission/organization';
import { useFluencyCourseDecks } from '../../reports/hooks/use-fluency-course-decks';
import { useUser } from '../../user-management';
import type { SitemapLevel } from '../modules/sitemaps/sitemap';
import { SITEMAP_LEVELS } from '../modules/sitemaps/sitemap';
import type { FlattenedSitemapItem, ProcessedSectionItem } from '../types';
import { NAV_TYPE, type NavType } from '../utils';
import { flattenSitemaps } from '../utils/process-helpers';
import { processSitemapTree } from './process-sitemap-tree';
import { useGetAdminSitemap } from './use-get-admin-sitemap';

type Props = {
  region: string | undefined;
  courseId?: string | undefined;
  courseOrder: number | undefined;
  level: SitemapLevel;
  locale: string | undefined;
  navType?: NavType;
  isRtiClassroom?: boolean;
  classroomId?: string;
};

export function useSitemapTree({
  region,
  courseId,
  courseOrder,
  level = SITEMAP_LEVELS.MANAGER,
  locale,
  navType = NAV_TYPE.Classroom,
  isRtiClassroom,
  classroomId,
}: Props) {
  const { t } = useTranslation();
  const { user, isStudentViewActive } = useUser();

  const {
    organization,
    loading: isLoadingOrganization,
    isPlatformEnabled: isPlatformEnabledOrg,
  } = useOrganization();

  const { permissionsByCurrentCourse, hasPermissionsForAdminDashboard } =
    usePermissionCourses({
      courseId,
    });

  const useOrgPermissionsIfClassroomDoesntHaveCourse = (): boolean => {
    if (classroomId && !courseId) {
      return isPlatformEnabledOrg || false;
    }
    return permissionsByCurrentCourse?.platformEnabled;
  };

  const { classroom } = useClassroom({
    classroomId,
    enabled: Boolean(user?.organizationId),
  });
  const { reports } = useClassroomCapabilities({
    classroom: classroom as Classroom,
  });

  const isPlatformEnabled = useOrgPermissionsIfClassroomDoesntHaveCourse();
  const isJourneys = isJourneysEnabled(organization as Organization);
  const viewAs = isStudentViewActive ? 'student' : undefined;

  const { value: showGeneralProgressToJourneyClassrooms } = useFlag(
    'showGeneralProgressToJourneyClassrooms',
    false
  );

  const variables = {
    region: region!,
    courseOrder: isJourneys ? undefined : courseOrder,
    level,
    viewAs,
  };

  const { data, isPending, isError } = useSitemapTreeQuery(variables, {
    queryKey: ['SitemapTree', { ...variables, locale }],
    enabled:
      (isJourneys || !!courseOrder) && !!region && !isLoadingOrganization,
    staleTime: Infinity,
  });

  const showAdminDashboard = hasPermissionsForAdminDashboard();
  const showIntervention = permissionsByCurrentCourse.earlyIntervention;
  const showAssessment =
    isJourneyClassroom(classroom as Classroom) ||
    permissionsByCurrentCourse.assessment;

  const { value: catchUpDashboardEnabled } = useFlag(
    'catchupdashbordenabled',
    false,
    {
      identifier: '',
      custom: {
        schoolId: user?.organizationId!,
        region: region!,
      },
    }
  );

  const isPrimaryClassroom = stages.isPrimaria(courseOrder);
  const showEarlyInterventionMenuItem = Boolean(
    catchUpDashboardEnabled && isRtiClassroom && isPrimaryClassroom
  );
  const isInStudentView = viewAs === 'student';

  const adminSitemap = useGetAdminSitemap({
    showAdminDashboard: showAdminDashboard,
  });

  const { hasDecks } = useFluencyCourseDecks({
    classroomId: classroomId || '',
  });

  const sitemapTree = useMemo(() => {
    if (navType === NAV_TYPE.Admin) {
      return adminSitemap;
    }

    return data?.sitemapTree
      ? processSitemapTree({
          tree: data.sitemapTree,
          navType,
          t,
          isInStudentView,
          hasFluencyDecks: hasDecks,
          isJourneyClassroom: isJourneyClassroom(classroom as Classroom),
          permissions: {
            assessmentAndIntervention: showAssessment || showIntervention,
            earlyIntervention: showEarlyInterventionMenuItem,
            platformEnabled: isPlatformEnabled || false,
            generalProgress:
              !isJourneys || showGeneralProgressToJourneyClassrooms,
            canAccessReports: reports.canAccessReports,
          },
        })
      : [];
  }, [
    navType,
    data?.sitemapTree,
    t,
    isInStudentView,
    hasDecks,
    classroom,
    showAssessment,
    showIntervention,
    showEarlyInterventionMenuItem,
    isPlatformEnabled,
    isJourneys,
    showGeneralProgressToJourneyClassrooms,
    reports.canAccessReports,
    adminSitemap,
  ]);

  const allRoutesSitemapTree = useMemo(() => {
    if (navType === NAV_TYPE.Admin) {
      return adminSitemap;
    }
    return data?.sitemapTree
      ? processSitemapTree(
          {
            tree: data.sitemapTree,
            navType,
            t,
            isInStudentView,
            hasFluencyDecks: hasDecks,
            isJourneyClassroom: isJourneyClassroom(classroom as Classroom),
            permissions: {
              assessmentAndIntervention: showAssessment || showIntervention,
              earlyIntervention: showEarlyInterventionMenuItem,
              platformEnabled: isPlatformEnabled || false,
              generalProgress:
                !isJourneys || showGeneralProgressToJourneyClassrooms,
              canAccessReports: reports.canAccessReports,
            },
          },
          true
        )
      : [];
  }, [
    navType,
    data?.sitemapTree,
    t,
    isInStudentView,
    hasDecks,
    showAssessment,
    showIntervention,
    showEarlyInterventionMenuItem,
    isPlatformEnabled,
    reports.canAccessReports,
    adminSitemap,
    classroom,
  ]);

  const flattenedSitemaps = useMemo(
    () => flattenSitemaps(sitemapTree),
    [sitemapTree]
  );

  const flattenedAllRoutes = useMemo(() => {
    return flattenSitemaps(allRoutesSitemapTree);
  }, [allRoutesSitemapTree]);

  const getSitemapByRoute = (
    route: string
  ): Exclude<ProcessedSectionItem, string> => {
    return flattenedAllRoutes?.find(
      (sitemap: FlattenedSitemapItem) =>
        typeof sitemap !== 'string' && route === sitemap?.route
    ) as Exclude<ProcessedSectionItem, string>;
  };

  const getAllSitemapRoutes = (): (string | undefined)[] => {
    if (!flattenedAllRoutes) {
      return [];
    }
    return flattenedAllRoutes?.map((sitemap: FlattenedSitemapItem) =>
      typeof sitemap !== 'string' ? sitemap?.route : undefined
    );
  };

  const getFirstSitemapRoute = (): string => {
    if (!flattenedSitemaps) {
      return '';
    }
    return flattenedSitemaps[0]?.route || '';
  };

  const getRouteByPageId = (pageId: string) => {
    return (
      flattenedSitemaps?.find(
        (sitemap: FlattenedSitemapItem) =>
          sitemap &&
          typeof sitemap !== 'string' &&
          'fullPageId' in sitemap &&
          'demoPageId' in sitemap &&
          (sitemap?.fullPageId === pageId || sitemap?.demoPageId === pageId)
      )?.route || ''
    );
  };

  return {
    isPending,
    isError,
    sitemapTree,
    getSitemapByRoute,
    getAllSitemapRoutes,
    getRouteByPageId,
    getFirstSitemapRoute,
  };
}
